/**
 * Smart layer invalidation system
 * Tracks which card properties affect which canvas layers
 * Only redraws layers that actually changed
 */

import { useRef, useCallback } from 'react';
import { Card } from 'src/model';

type LayerName = 
    | 'frame' 
    | 'artwork' 
    | 'name' 
    | 'effect' 
    | 'pendulumEffect' 
    | 'stat' 
    | 'setId' 
    | 'password' 
    | 'creator' 
    | 'sticker' 
    | 'pendulumScale'
    | 'cardIcon'
    | 'overlay';

type LayerDependencies = {
    [K in LayerName]: (keyof Card)[];
};

// Define which card properties affect which layers
const LAYER_DEPENDENCIES: LayerDependencies = {
    frame: [
        'frame', 'attribute', 'star', 'isPendulum', 'isLink', 'leftFrame', 'rightFrame',
        'pendulumFrame', 'pendulumRightFrame', 'foil', 'finish', 'artFinish', 'otherFinish',
        'opacity', 'hasBackground', 'backgroundType', 'dyeList', 'linkMap', 'cardIcon',
        'subFamily', 'starAlignment', 'starList', 'pendulumSize', 'legacyTemplate',
        'isDuelTerminalCard', 'isSpeedCard', 'linkRating', 'flag', 'art', 'background'
    ],
    artwork: ['art', 'artData', 'artFit', 'artStyle', 'artSource', 'artCrop'],
    name: ['name', 'nameStyle', 'nameStyleType', 'foil', 'format', 'frame', 'furiganaHelper'],
    effect: ['effect', 'effectStyle', 'effectTextStyle', 'typeAbility', 'format', 'furiganaHelper'],
    pendulumEffect: ['pendulumEffect', 'pendulumStyle', 'pendulumTextStyle', 'pendulumSize', 'format', 'furiganaHelper'],
    stat: ['atk', 'def', 'linkRating', 'statTextStyle', 'linkMap', 'flag'],
    setId: ['setId', 'format', 'otherTextStyle'],
    password: ['password', 'format', 'otherTextStyle', 'firstEditionText', 'cornerText', 'hasCornerText', 'isFirstEdition'],
    creator: ['creator', 'format', 'otherTextStyle', 'isLimitedEdition'],
    sticker: ['sticker', 'otherFinish'],
    pendulumScale: ['pendulumScaleBlue', 'pendulumScaleRed', 'pendulumSize', 'pendulumTextStyle'],
    cardIcon: ['cardIcon', 'subFamily', 'otherFinish'],
    overlay: ['linkMap', 'foil', 'finish', 'flag', 'isPendulum', 'opacity'],
};

export const useLayerInvalidation = () => {
    const previousCardRef = useRef<Partial<Card>>({});
    const invalidLayersRef = useRef<Set<LayerName>>(new Set());

    const checkInvalidation = useCallback((currentCard: Card): Set<LayerName> => {
        const invalidLayers = new Set<LayerName>();
        const prevCard = previousCardRef.current;

        // First render - invalidate all layers
        if (Object.keys(prevCard).length === 0) {
            Object.keys(LAYER_DEPENDENCIES).forEach(layer => 
                invalidLayers.add(layer as LayerName)
            );
            previousCardRef.current = { ...currentCard };
            return invalidLayers;
        }

        // Check each layer's dependencies
        Object.entries(LAYER_DEPENDENCIES).forEach(([layer, dependencies]) => {
            const hasChanged = dependencies.some(dep => {
                const prev = prevCard[dep];
                const curr = currentCard[dep];
                
                // Deep comparison for arrays and objects
                if (Array.isArray(prev) && Array.isArray(curr)) {
                    return JSON.stringify(prev) !== JSON.stringify(curr);
                }
                if (typeof prev === 'object' && typeof curr === 'object' && prev !== null && curr !== null) {
                    return JSON.stringify(prev) !== JSON.stringify(curr);
                }
                return prev !== curr;
            });

            if (hasChanged) {
                invalidLayers.add(layer as LayerName);
            }
        });

        previousCardRef.current = { ...currentCard };
        invalidLayersRef.current = invalidLayers;
        return invalidLayers;
    }, []);

    const shouldRedraw = useCallback((layer: LayerName): boolean => {
        return invalidLayersRef.current.has(layer);
    }, []);

    const markAllInvalid = useCallback(() => {
        Object.keys(LAYER_DEPENDENCIES).forEach(layer => 
            invalidLayersRef.current.add(layer as LayerName)
        );
    }, []);

    const clearInvalidation = useCallback(() => {
        invalidLayersRef.current.clear();
    }, []);

    return {
        checkInvalidation,
        shouldRedraw,
        markAllInvalid,
        clearInvalidation,
    };
};
